import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Sales } from './pages/Sales';
import { HR } from './pages/HR';
import { Legal } from './pages/Legal';
import { Compliance } from './pages/Compliance';
import { Payroll } from './pages/Payroll';
import { Marketing } from './pages/Marketing';
import { PayrollRecordForm } from './pages/payroll/PayrollRecordForm';
import { BenefitForm } from './pages/payroll/BenefitForm';
import { Deductions } from './pages/payroll/Deductions';
import { DeductionForm } from './pages/payroll/DeductionForm';
import { CampaignForm } from './pages/marketing/CampaignForm';
import { ComplianceRecordForm } from './pages/compliance/ComplianceRecordForm';
import { Documents } from './pages/Documents';
import { DocumentForm } from './pages/documents/DocumentForm';
import { Vendors } from './pages/Vendors';
import { Training } from './pages/Training';
import { CourseForm } from './pages/training/CourseForm';
import { CourseDetail } from './pages/training/CourseDetail';
import { AssignmentForm } from './pages/training/AssignmentForm';
import { VendorForm } from './pages/vendors/VendorForm';
import { VendorDetail } from './pages/vendors/VendorDetail';
import { PurchaseOrderForm } from './pages/vendors/PurchaseOrderForm';
import { PurchaseOrderDetail } from './pages/vendors/PurchaseOrderDetail';
import { InvoiceForm } from './pages/vendors/InvoiceForm';
import { InvoiceDetail } from './pages/vendors/InvoiceDetail';
import { Expenses } from './pages/Expenses';
import { ExpenseForm } from './pages/expenses/ExpenseForm';
import { ExpenseDetail } from './pages/expenses/ExpenseDetail';
import { ExpenseReportForm } from './pages/expenses/ExpenseReportForm';
import { ExpenseReportDetail } from './pages/expenses/ExpenseReportDetail';
import { ExpenseAnalytics } from './pages/expenses/ExpenseAnalytics';
import FirebaseBillingSync from './pages/FirebaseBillingSync';
import BillingToExpenseConversion from './pages/expenses/BillingToExpenseConversion';
import SeedTrainingAndDocuments from './pages/SeedTrainingAndDocuments';
import { Contracts } from './pages/Contracts';
import { EmployeeForm } from './pages/hr/EmployeeForm';
import { EmployeeDetail } from './pages/hr/EmployeeDetail';
import { TimeOffForm } from './pages/hr/TimeOffForm';
import { LeadForm } from './pages/sales/LeadForm';
import { AccountForm } from './pages/sales/AccountForm';
import { ContactForm } from './pages/sales/ContactForm';
import { OpportunityForm } from './pages/sales/OpportunityForm';
import { ImportLeads } from './pages/sales/ImportLeads';
import { LeadDetail } from './pages/sales/LeadDetail';
import { ScrapeRestaurants } from './pages/sales/ScrapeRestaurants';
import { ApprovalQueue } from './pages/sales/ApprovalQueue';
import { ScrapingJobs } from './pages/sales/ScrapingJobs';
import { ScrapingJobStatus } from './pages/sales/ScrapingJobStatus';
import { ProviderManagement } from './pages/ProviderManagement';
import { ProviderDetail } from './pages/ProviderDetail';
import { EmployerManagement } from './pages/EmployerManagement';
import { EmployerDetail } from './pages/EmployerDetail';
import { UserManagement } from './pages/UserManagement';
import { BusinessDocuments } from './pages/BusinessDocuments';
import { BusinessDocumentForm } from './pages/BusinessDocumentForm';
import { FinancialDashboard } from './pages/FinancialDashboard';
import { FundingApplications } from './pages/FundingApplications';
import { FundingApplicationForm } from './pages/financial/FundingApplicationForm';
import { FundingApplicationDetail } from './pages/financial/FundingApplicationDetail';
import { Helpdesk } from './pages/Helpdesk';
import { FeatureRequests } from './pages/FeatureRequests';
import { Analytics } from './pages/Analytics';
import { AuditLogs } from './pages/AuditLogs';
import { ReceiptReview } from './pages/ReceiptReview';
import { PasswordVault } from './pages/PasswordVault';
import { GoogleAdsCallback } from './pages/marketing/GoogleAdsCallback';
import { TeamWorkspace } from './pages/team/TeamWorkspace';
import { MeetingUpload } from './pages/team/MeetingUpload';
import { MeetingDetail } from './pages/team/MeetingDetail';
import { TasksKanban } from './pages/team/TasksKanban';
import { TaskForm } from './pages/team/TaskForm';
import { DecisionsBoard } from './pages/team/DecisionsBoard';
import { RisksRegister } from './pages/team/RisksRegister';
import { QuestionsBoard } from './pages/team/QuestionsBoard';
import { EnrollmentDashboard } from './pages/EnrollmentDashboard';
import { MemberDetail } from './pages/MemberDetail';
import { EarlyAccessAdmin } from './pages/EarlyAccessAdmin';
import { PilotCrmLeads } from './pages/pilot-crm/PilotCrmLeads';
import { PilotCrmLeadDetail } from './pages/pilot-crm/PilotCrmLeadDetail';
import { PilotCrmTasks } from './pages/pilot-crm/PilotCrmTasks';
import { PilotCrmDashboard } from './pages/pilot-crm/PilotCrmDashboard';
import { PilotCrmKanban } from './pages/pilot-crm/PilotCrmKanban';
import { OperationsRole } from './models/user.model';
import './App.css';

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/enrollment"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EnrollmentDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/enrollment/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MemberDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Sales />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-crm"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PilotCrmLeads />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-crm/leads/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PilotCrmLeadDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-crm/tasks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PilotCrmTasks />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-crm/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PilotCrmDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/pilot-crm/kanban"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PilotCrmKanban />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/leads/new"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <LeadForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/leads/:id"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <LeadDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/leads/:id/edit"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <LeadForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/scrape"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <ScrapeRestaurants />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/scraping-jobs"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <ScrapingJobs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/scraping-jobs/:id"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <ScrapingJobStatus />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/approval-queue"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <ApprovalQueue />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/import"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <ImportLeads />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/accounts/new"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <AccountForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/accounts/:id"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <AccountForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/contacts/new"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <ContactForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/contacts/:id"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <ContactForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/opportunities/new"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <OpportunityForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales/opportunities/:id"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.sales, OperationsRole.salesManager, OperationsRole.operationsAdmin, OperationsRole.executive]}>
                  <Layout>
                    <OpportunityForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr"
              element={
                <ProtectedRoute>
                  <Layout>
                    <HR />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/employees/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/timeoff/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TimeOffForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hr/timeoff/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TimeOffForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/legal"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Legal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Compliance />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance/records/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ComplianceRecordForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance/records/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ComplianceRecordForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Payroll />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/records/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollRecordForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/records/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollRecordForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/benefits/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BenefitForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/benefits/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BenefitForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/deductions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Deductions />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/deductions/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeductionForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/deductions/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeductionForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/deductions/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DeductionForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Marketing />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing/campaigns/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CampaignForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/marketing/campaigns/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CampaignForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/providers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProviderManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/providers/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProviderDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employers"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployerManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/employers/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployerDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FinancialDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial/funding"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FundingApplications />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial/funding/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FundingApplicationForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial/funding/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FundingApplicationDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial/funding/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FundingApplicationForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/helpdesk"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Helpdesk />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feature-requests"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FeatureRequests />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Analytics />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AuditLogs />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/receipt-review"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ReceiptReview />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/password-vault"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PasswordVault />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Documents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DocumentForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/documents/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DocumentForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Vendors />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VendorForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VendorDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VendorForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/purchase-orders/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PurchaseOrderForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/purchase-orders/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PurchaseOrderForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/purchase-orders/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PurchaseOrderDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/invoices/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <InvoiceForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/invoices/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <InvoiceForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/invoices/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <InvoiceDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Training />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/courses/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CourseForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/courses/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CourseDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/courses/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CourseForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/training/assignments/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AssignmentForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/seed-training-documents"
              element={
                <ProtectedRoute requiredRoles={[OperationsRole.operationsAdmin]}>
                  <Layout>
                    <SeedTrainingAndDocuments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Expenses />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/analytics"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseAnalytics />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/firebase-billing"
              element={
                <ProtectedRoute>
                  <Layout>
                    <FirebaseBillingSync />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/billing-conversion"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BillingToExpenseConversion />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/reports/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseReportForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/reports/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseReportDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/reports/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseReportForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ExpenseForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Contracts />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-documents"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BusinessDocuments />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-documents/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BusinessDocumentForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/business-documents/:id/edit"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BusinessDocumentForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TeamWorkspace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/upload"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MeetingUpload />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/meetings/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MeetingDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/tasks/new"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/tasks/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskForm />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/tasks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TasksKanban />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/decisions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DecisionsBoard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/risks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RisksRegister />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/team-workspace/questions"
              element={
                <ProtectedRoute>
                  <Layout>
                    <QuestionsBoard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/early-access"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EarlyAccessAdmin />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/auth/google-ads/callback"
              element={
                <ProtectedRoute>
                  <GoogleAdsCallback />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;

